import { useMemo } from 'react';

import { useUserActivity } from '@/hooks/query-hooks/gaia-api/activity';
import { useUserState } from '@/hooks/use-user-state';

/**
 * Provide the set facility IDs that are positively known to be offline.
 * A facility is offline if at least one of its agents is offline.
 *
 * @return A map of facility_id to presence status (online or offline).
 */
export const useOfflineFacilities = (): {
  offlineFacilities: Set<string>;
  offlineFacilitiesByOrg: Record<string, string[]>;
  getOfflineFacilityCountByOrg: (orgId: string) => number;
} => {
  const { data: userState } = useUserState();
  const { data: agentPresence } = useUserActivity();

  const facilities = userState?.facilities?.unordered;

  const offlineFacilities = useMemo(
    () =>
      new Set(
        (facilities ?? [])
          .filter((uf) =>
            uf.agents.some(
              (a) => agentPresence?.find((ap) => ap.agentId === a.agentId)?.status === 'offline'
            )
          )
          .map((uf) => uf.facilityId)
      ),
    [facilities, agentPresence]
  );

  const offlineFacilitiesByOrg = useMemo(() => {
    const orgMap: Record<string, string[]> = {};

    facilities?.forEach((facility) => {
      if (offlineFacilities.has(facility.facilityId)) {
        const orgId = facility.organizationId;
        if (!orgMap[orgId]) {
          orgMap[orgId] = [];
        }
        orgMap[orgId].push(facility.facilityId);
      }
    });

    return orgMap;
  }, [offlineFacilities, facilities]);

  const getOfflineFacilityCountByOrg = (orgId: string) => {
    return offlineFacilitiesByOrg[orgId]?.length ?? 0;
  };

  return {
    offlineFacilities,
    offlineFacilitiesByOrg,
    getOfflineFacilityCountByOrg,
  };
};
